import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { InfoBox } from 'components/home/InfoBox';
import { TodayInfo } from 'components/home/TodayInfo';
import { HourContainer } from 'components/home/HourContainer';
import Footer from 'components/home/Footer';
import weatherInfoService from 'services/weather/weather-info-service';

export function HomePage(props) {
  const [location, setLocation] = useState({
    loaded: false,
    coordinates: { lat: "", lng: "" },
    error: null,
  });
  const [weatherData, setWeatherData] = useState({
    currentLocation: {
      loading: true,
      data: null,
      error: null
    },
    forecastThree: {
      loading: true,
      data: null,
      error: null
    }
  });

  // Memoize data for child components
  const { currentLocationData, loadingCurrentLocationData, currentLocationError } = useMemo(() => ({
    currentLocationData: weatherData.currentLocation.data,
    loadingCurrentLocationData: weatherData.currentLocation.loading,
    currentLocationError: weatherData.currentLocation.error
  }), [weatherData.currentLocation]);

  const { forecastThreeData, loadingForecastThreeData, forecastThreeError } = useMemo(() => ({
    forecastThreeData: weatherData.forecastThree.data,
    loadingForecastThreeData: weatherData.forecastThree.loading,
    forecastThreeError: weatherData.forecastThree.error
  }), [weatherData.forecastThree]);

  const onLocationSuccess = useCallback((position) => {
    setLocation({
      loaded: true,
      coordinates: {
        lat: position.coords.latitude,
        lng: position.coords.longitude,
      },
      error: null,
    });
  }, []);

  const onLocationError = useCallback((error) => {
    let message = "An unknown error occurred";
    
    // Provide more specific error messages
    if (error.code === 1) {
      message = "Location permission denied";
    } else if (error.code === 2) {
      message = "Location unavailable";
    } else if (error.code === 3) {
      message = "Location request timed out";
    } else if (error.code === 0) {
      message = error.message;
    }
    
    setLocation({
      loaded: true,
      coordinates: { lat: "", lng: "" },
      error: {
        ...error,
        message
      },
    });
  }, []);

  const fetchCurrentLocationData = useCallback(async () => {
    setWeatherData(prev => ({
      ...prev,
      currentLocation: { ...prev.currentLocation, loading: true, error: null }
    }));
    
    try {
      const response = await weatherInfoService.getLocationInfo(location.coordinates.lat, location.coordinates.lng);
      
      if (response && response.success && response.data) {
        const data = response.data.WEATHER.AREA;
        setWeatherData(prev => ({
          ...prev,
          currentLocation: { loading: false, data, error: null }
        }));
        return data;
      } else {
        throw new Error(response.error || "Failed to fetch location data");
      }
    } catch (error) {
      setWeatherData(prev => ({
        ...prev,
        currentLocation: { loading: false, data: null, error: error.message }
      }));
      return null;
    }
  }, [location.coordinates.lat, location.coordinates.lng]);

  const fetchForecastThreeData = useCallback(async (regionCode) => {
    if (!regionCode) {
      setWeatherData(prev => ({
        ...prev,
        forecastThree: { 
          loading: false, 
          data: null, 
          error: "Region code is required to fetch forecast data" 
        }
      }));
      return;
    }

    setWeatherData(prev => ({
      ...prev,
      forecastThree: { ...prev.forecastThree, loading: true, error: null }
    }));
    
    try {
      const response = await weatherInfoService.getWeatherForecastThree(regionCode);
      
      if (response && response.success && response.data) {
        setWeatherData(prev => ({
          ...prev,
          forecastThree: { 
            loading: false, 
            data: response.data.FcastReport, 
            error: null 
          }
        }));
      } else {
        throw new Error(response.error || "Failed to fetch forecast data");
      }
    } catch (error) {
      setWeatherData(prev => ({
        ...prev,
        forecastThree: { 
          loading: false, 
          data: null, 
          error: error.message 
        }
      }));
    }
  }, []);

  // Handle geolocation
  useEffect(() => {
    if (!navigator.geolocation) {
      onLocationError({
        code: 0,
        message: "Geolocation not supported",
      });
      return;
    }
    
    const geoWatchId = navigator.geolocation.getCurrentPosition(
      onLocationSuccess, 
      onLocationError, 
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 60000
      }
    );
    
    // Clean up geolocation watch
    return () => {
      if (geoWatchId && navigator.geolocation) {
        navigator.geolocation.clearWatch(geoWatchId);
      }
    };
  }, [onLocationSuccess, onLocationError]);

  // Fetch data when location changes
  useEffect(() => {
    if (!location.loaded || !location.coordinates.lat || !location.coordinates.lng) {
      return;
    }

    const fetchData = async () => {
      const data = await fetchCurrentLocationData();
      if (data) {
        await fetchForecastThreeData(data.townFcastRgnCd);
      }
    };
    
    fetchData();
  }, [location, fetchCurrentLocationData, fetchForecastThreeData]);

  // Create memoized retry handlers
  const handleRetryCurrentLocation = useCallback(() => {
    fetchCurrentLocationData();
  }, [fetchCurrentLocationData]);

  const handleRetryForecast = useCallback(() => {
    fetchForecastThreeData(currentLocationData && currentLocationData.townFcastRgnCd);
  }, [fetchForecastThreeData, currentLocationData]);

  return (
    <Container>
      <InfoBox 
        loading={loadingCurrentLocationData} 
        data={currentLocationData}
        error={currentLocationError} 
        onRetry={handleRetryCurrentLocation}
      />
      <TodayInfo 
        loading={loadingCurrentLocationData} 
        data={currentLocationData}
        error={currentLocationError} 
        onRetry={handleRetryCurrentLocation} 
      />
      <HourContainer 
        loading={loadingForecastThreeData} 
        data={forecastThreeData}
        error={forecastThreeError} 
        onRetry={handleRetryForecast}
      />
      <Footer />
    </Container>
  );
}

const Container = styled.div`
  padding: 20px;
`;